import { useState, lazy, Suspense } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useSamplingEvent } from "@/hooks/use-sampling-events";

type SamplingEvent = components["schemas"]["SamplingEvent"];
import { formatDateOnly, effectiveEventDate } from "@/lib/dates";
import { EventMetadataCard } from "@/components/sampling/event-metadata-card";
import { EventStatusActions } from "@/components/sampling/event-status-actions";
import { LabsForEventTab } from "@/components/sampling/labs-for-event-tab";
import { samplingStatusVariant } from "@/components/sampling/sampling-event-columns";
import { permitTypeVariant } from "@/components/permits/permit-columns";
import {
  PageHeader,
  Breadcrumbs,
  PageSkeleton,
  Badge,
  Skeleton,
  Card,
  CardHeader,
  CardContent,
} from "@/components/ui";

// Lazy load Results tab (heavy component with batch entry grid)
const LazyEventResultsTab = lazy(() =>
  import("@/components/sampling/event-results-tab").then((mod) => ({
    default: mod.EventResultsTab,
  })),
);

export const Route = createFileRoute("/_authenticated/sampling-events/$eventId/")({
  component: SamplingEventDetailPage,
});

type DetailTab = "overview" | "results" | "labs";

function SamplingEventDetailPage() {
  const { t } = useTranslation(["sampling", "common", "permits", "labsForEvent"]);
  const { eventId } = Route.useParams();

  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canReopen = can("sampling_events", "update");

  const [activeTab, setActiveTab] = useState<DetailTab>("overview");

  const { data: event, isLoading, isError, error } = useSamplingEvent(orgId, eventId);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="p-4 sm:p-8">
        <Breadcrumbs items={[{ label: t("title"), to: "/sampling-events" }]} />
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("unknownError", { ns: "common" })}
        </div>
      </div>
    );
  }

  const eventLabel = event.facility_name
    ? `${event.facility_name} — ${(() => {
        const d = effectiveEventDate(event);
        return d ? formatDateOnly(d) : eventId.slice(0, 8);
      })()}`
    : t("detailTitle");

  return (
    <div className="p-4 sm:p-8">
      <Breadcrumbs items={[{ label: t("title"), to: "/sampling-events" }, { label: eventLabel }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageHeader
            title={
              event.facility_name && event.monitoring_location_name
                ? `${event.facility_name} — ${event.monitoring_location_name}`
                : t("detailTitle")
            }
          />
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={samplingStatusVariant(event.status)}>
              {t(`statusLabels.${event.status}`)}
            </Badge>
            <Badge variant="info">{t(`eventTypes.${event.event_type}`)}</Badge>
          </div>
        </div>
        {orgId && (
          <EventStatusActions
            orgId={orgId}
            eventId={eventId}
            currentStatus={event.status}
            canReopen={canReopen}
          />
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex border-b border-gray-200 dark:border-gray-700">
        <TabButton active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>
          {t("tabs.overview", { ns: "common", defaultValue: "Overview" })}
        </TabButton>
        <TabButton active={activeTab === "results"} onClick={() => setActiveTab("results")}>
          {t("results.title")}
        </TabButton>
        <TabButton active={activeTab === "labs"} onClick={() => setActiveTab("labs")}>
          {t("labsForEvent:tab.label")}
        </TabButton>
      </div>

      <div className="mt-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <EventMetadataCard event={event} />
            <LinkedPermitsCard permits={event.linked_permits} />
          </div>
        )}
        {activeTab === "results" && (
          <Suspense fallback={<Skeleton className="h-64 w-full" />}>
            <LazyEventResultsTab
              eventId={eventId}
              permitId={event.permit_id}
              eventStatus={event.status}
            />
          </Suspense>
        )}
        {activeTab === "labs" && <LabsForEventTab orgId={orgId} eventId={eventId} />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? "border-b-2 border-primary text-primary"
          : "text-muted hover:text-on-surface hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
      }`}
    >
      {children}
    </button>
  );
}

type LinkedPermit = NonNullable<SamplingEvent["linked_permits"]>[number];

function LinkedPermitsCard({ permits }: { permits?: LinkedPermit[] | null }) {
  const { t } = useTranslation(["sampling", "permits"]);

  if (!permits || permits.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-on-surface">
            {t("sampling:fields.linkedPermits")}
          </h2>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">{t("sampling:fields.noLinkedPermits")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-on-surface">
          {t("sampling:fields.linkedPermits")}
        </h2>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {permits.map((permit) => (
            <li key={permit.permit_id} className="flex items-center gap-2 text-sm text-on-surface">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <Link
                to="/permits/$permitId"
                params={{ permitId: permit.permit_id ?? "" }}
                className="text-primary hover:underline"
              >
                {permit.permit_number ?? permit.permit_id}
              </Link>
              {permit.permit_type && (
                <Badge variant={permitTypeVariant(permit.permit_type)}>
                  {t(`permits:types.${permit.permit_type}`, {
                    ns: "permits",
                    defaultValue: permit.permit_type,
                  })}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
